import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Pagination,
  Grid,
  IconButton,
  Tooltip,
  Checkbox,
  Menu,
  ListItemText
} from '@mui/material';
import {
  Edit as EditIcon,
  CloudSync as EnrichIcon,
  MoreVert as MoreIcon,
  GetApp as ExportIcon
} from '@mui/icons-material';
import { locationService, Location, BulkLocationAction } from '../services/locationService';

const LocationManagement: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selection state
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Location>>({});
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadLocations();
  }, [page, statusFilter, categoryFilter]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await locationService.getAllLocations({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        search: searchQuery || undefined,
        page,
        page_size: 20
      });
      
      setLocations(response.locations);
      setTotalPages(response.total_pages);
      setError(null);
    } catch (err) {
      setError('Failed to load locations');
      console.error('Error loading locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadLocations();
  };

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setEditFormData({
      name: location.name,
      description: location.description,
      category: location.category,
      tags: location.tags
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedLocation) return;

    try {
      setEditLoading(true);
      await locationService.updateLocation(selectedLocation.id, editFormData);
      setEditDialogOpen(false);
      setSelectedLocation(null);
      setEditFormData({});
      await loadLocations();
    } catch (err) {
      setError('Failed to update location');
      console.error('Error updating location:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEnrichLocation = async (locationId: string) => {
    try {
      await locationService.enrichLocation(locationId);
      await loadLocations();
    } catch (err) {
      setError('Failed to enrich location');
      console.error('Error enriching location:', err);
    }
  };

  const handleSelectLocation = (locationId: string) => {
    setSelectedLocations(prev => 
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLocations.length === locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(locations.map(loc => loc.id));
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedLocations.length === 0) return;

    try {
      const bulkAction: BulkLocationAction = {
        location_ids: selectedLocations,
        action: action as any,
        reason: `Bulk ${action} action performed by admin`
      };

      await locationService.performBulkAction(bulkAction);
      setSelectedLocations([]);
      setBulkActionMenuAnchor(null);
      await loadLocations();
    } catch (err) {
      setError(`Failed to perform bulk ${action}`);
      console.error(`Error performing bulk ${action}:`, err);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const blob = await locationService.exportLocations({
        format,
        status: statusFilter || undefined,
        category: categoryFilter || undefined
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `locations_export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export locations');
      console.error('Error exporting locations:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'suspended': return 'error';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && locations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Location Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="restaurant">Restaurant</MenuItem>
                  <MenuItem value="market">Market</MenuItem>
                  <MenuItem value="cafe">Cafe</MenuItem>
                  <MenuItem value="food_truck">Food Truck</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={loading}
                fullWidth
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={() => handleExport('csv')}
                  size="small"
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={() => handleExport('json')}
                  size="small"
                >
                  Export JSON
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Bulk Actions */}
          {selectedLocations.length > 0 && (
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={(e) => setBulkActionMenuAnchor(e.currentTarget)}
              >
                Bulk Actions ({selectedLocations.length} selected)
              </Button>
              <Menu
                anchorEl={bulkActionMenuAnchor}
                open={Boolean(bulkActionMenuAnchor)}
                onClose={() => setBulkActionMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleBulkAction('activate')}>
                  <ListItemText>Activate</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleBulkAction('suspend')}>
                  <ListItemText>Suspend</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleBulkAction('enrich')}>
                  <ListItemText>Enrich with External Data</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Locations Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedLocations.length === locations.length && locations.length > 0}
                    indeterminate={selectedLocations.length > 0 && selectedLocations.length < locations.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Reviews</TableCell>
                <TableCell>Hunt Usage</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.map((location) => (
                <TableRow key={location.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLocations.includes(location.id)}
                      onChange={() => handleSelectLocation(location.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {location.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {location.address.city}, {location.address.state}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {location.category && (
                      <Chip label={location.category} size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={location.status}
                      color={getStatusColor(location.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {location.stats.average_rating.toFixed(1)}
                  </TableCell>
                  <TableCell>{location.stats.total_reviews}</TableCell>
                  <TableCell>{location.stats.hunt_usage_count}</TableCell>
                  <TableCell>{formatDate(location.created_at)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditLocation(location)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Enrich with External Data">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEnrichLocation(location.id)}
                        >
                          <EnrichIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Location</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              fullWidth
              label="Name"
              value={editFormData.name || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={editFormData.description || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Category"
              value={editFormData.category || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={editFormData.tags?.join(', ') || ''}
              onChange={(e) => setEditFormData(prev => ({ 
                ...prev, 
                tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
              }))}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationManagement;